import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
import BusinessProfileAppResource from './BusinessProfileAppResource';
import CalendarEventAppResource from './CalendarEventAppResource';
import ContactAppResource from './ContactAppResource';
import OrganizationAppResource from './OrganizationAppResource';
import PropertyAppResource from './PropertyAppResource';
import PropertyInstanceAppResource from './PropertyInstanceAppResource';
import UserAppResource from './UserAppResource';
import Engine from '@milestoneslabs/milestones-js-engine/src/Engine';
import get from 'lodash/get';
import AgentContactAppResource from "./AgentContactAppResource";

class AddressAppResource extends AppResource {

    /**
     * @returns {string}
     */
    appResourceType() {
        return 'Address';
    }

    /**
     * @returns {string}
     */
    appResourceApiPrefix() {
        return 'addresses';
    }

    /**
     * @returns {*[]}
     */
    hidden() {
        return super.hidden().concat([
            'uuid',
            'deleted_at',
        ]);
    }

    /**
     * @returns {*[]}
     */
    fillable() {
        return [
            'country',
            'county',
            'city',
            'state',
            'street',
            'streetNumber',
            'postalCode',
            'building',
            'floor',
            'flatNumber',
            'fullAddress',
            'direction',
            'preDirection',
            'postDirection',
            'calendarEvents',
            'users',
            'organizations',
            'properties',
            'propertyInstances',
            'businessProfiles',
            'contacts',
            'agentContacts',
            'suffix',
            'secondaryNumber',
            'latitude',
            'longitude',
            'deliveryLine',
            'corelogicClip',
        ];
    }

    /**
     * @returns {*[]}
     */
    relationMap() {
        return {
            calendarEvents: CalendarEventAppResource,
            users: UserAppResource,
            organizations: OrganizationAppResource,
            properties: PropertyAppResource,
            propertyInstances: PropertyInstanceAppResource,
            businessProfiles: BusinessProfileAppResource,
            contacts: ContactAppResource,
            agentContacts: AgentContactAppResource,
        };
    }

    /**
     * @returns {*[]}
     */
    relationForeignKeys() {
        return [];
    }

    /**
     * @returns {*}
     */
    validationRules() {
        return {
            country: {
                type: 'string',
                presence: false,
                length: {
                    minimum: 1,
                    maximum: 60,
                },
            },
            county: {
                type: 'string',
                presence: false,
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            city: {
                type: 'string',
                presence: false,
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            state: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            street: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            streetNumber: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            postalCode: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            building: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            floor: {
                numericality: {
                    notLessThanOrEqualTo: 1,
                    notGreaterThanOrEqualTo: 5,
                },
            },
            flatNumber: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 5,
                },
            },
            fullAddress: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            direction: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            preDirection: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 5,
                },
            },
            postDirection: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 5,
                },
            },
            suffix: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            secondaryNumber: {
                presence: false,
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            latitude: {
                presence: false,
            },
            longitude: {
                presence: false,
            },
            deliveryLine: {
                presence: false,
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            corelogicClip: {
                type: 'string'
            },
        };
    }

    apiPaths() {
        return {
            ...super.apiPaths(),
            parse: Engine.apiUrl(`${this.appResourceApiPrefix()}/parse`),
        };
    }

    async parse(rawAddress) {
        const url = this.apiPaths().parse;
        const apiResponse = await this.enginePost(url, {
            address: rawAddress,
        });

        const address = get(apiResponse, 'rawData.data');

        return address;
    }

    getGenericDeliveryLine() {
        if (this.deliveryLine) {
            return this.deliveryLine;
        }

        let deliveryLine = (this.street?.toLowerCase())?.replace(/\b\w/g, l => l.toUpperCase());

        if (this.preDirection) {
            deliveryLine = `${this.preDirection ?? ''} ${deliveryLine}`.trim();
        }

        if (this.suffix) {
            deliveryLine = `${deliveryLine} ${this.suffix ?? ''}`.trim();
        }

        if (this.postDirection) {
            deliveryLine = `${deliveryLine} ${this.postDirection ?? ''}`.trim();
        }

        if (this.streetNumber) {
            deliveryLine =  `${this.streetNumber ?? ''} ${deliveryLine}`.trim();
        }

        return deliveryLine;
    }

    generateFullAddress() {
        let fullAddress = '';
        const street = this.getGenericDeliveryLine();

        if (street) {
            fullAddress = `${street ?? ''}`.trim();
        }

        if (fullAddress && this.city) {
            fullAddress = `${fullAddress}, ${this.city ?? ''}`.trim();
        }

        if (fullAddress && this.state) {
            fullAddress = `${fullAddress}, ${this.state ?? ''}`.trim();
        }

        if (fullAddress === '') {
            fullAddress = this.fullAddress;
        }

        this.setAttribute('fullAddress', fullAddress);
    }
}

export default AddressAppResource;
